import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Alert } from '@mui/material';
import { debounce } from 'lodash';
import { Form } from 'src/components/shared';

import * as enrolmentActions from '../../../actions/enrolmentActions';
import { getInputReplacementVariables } from 'src/modules/app/actions/appSelector';
import { checkHasReplacementVariable, useStepError } from '../../../utils/sequenceHelper';
import { useSingleDebouncedFunction } from '../utlis';

const Email = React.memo(function Email({
  task,
  taskId,
  contact,
  formProps,
  formRef,
  assignedTo,
  data,
}) {
  const dispatch = useDispatch();
  const [editorRef, setEditorRef] = useState(null);

  const [loading, setLoading] = useState(false);
  const textEditorRef = useRef(null);
  const replacementVariables = useSelector((state) => getInputReplacementVariables(state));

  const moveCursorToEnd = () => {
    try {
      editorRef.current.selection.select(editorRef.current.getBody(), true);
      editorRef.current.selection.collapse(false);
      editorRef.current.focus();
    } catch (error) {
      console.log(error);
    }
  };

  const { debouncedFunction, cancel } = useSingleDebouncedFunction();

  const onChange = async (value, type) => {
    if (formRef.current) {
      let data = formRef.current.values;
      data = data[taskId];
      const templateId = task?.template?.id || '';
      const isContent = type === 'content';

      cancel();

      const hasReplacementVariable = checkHasReplacementVariable(value);

      if (hasReplacementVariable) {
        const executeDefault = () =>
          populateTemplate(
            contact.id,
            templateId,
            isContent ? data.subject : value,
            isContent ? value : data.content,
            assignedTo,
          );
        debouncedFunction(executeDefault, 1000)();
      }

      if (isContent && hasReplacementVariable) {
        setTimeout(() => {
          moveCursorToEnd();
        }, 100);
      }

      const customWaitTime = 4000;
      const executeCustom = () =>
        populateTemplate(
          contact.id,
          templateId,
          isContent ? data.subject : value,
          isContent ? value : data.content,
          assignedTo,
        );

      debouncedFunction(executeCustom, customWaitTime)();
    }
  };

  const getChildEditorRef = (editorRef) => {
    if (editorRef && editorRef !== null) {
      setEditorRef(editorRef);
    }
  };

  useEffect(() => {
    const templateId = task?.template?.id || '';
    let data = formRef.current.values;
    data = data[taskId];
    if (templateId && templateId !== '' && contact && contact.id && contact.id !== '') {
      populateTemplate(contact.id, templateId, data?.subject, data?.content);
    }
  }, [contact]);

  const errorString = useStepError(task, data, contact);

  const populateTemplate = useCallback(
    (contactId, templateId, subject, content) => {
      return new Promise((resolve, reject) => {
        dispatch(
          enrolmentActions.fetchPopulateEnqueueTemplate(
            taskId,
            contactId,
            templateId,
            subject,
            content,
            assignedTo,
            resolve,
            reject,
          ),
        );
      });
    },
    [dispatch, taskId, assignedTo],
  );

  return (
    <Box mb={2} mt={2}>
      {errorString && errorString !== '' && !loading ? (
        <Alert severity="error" sx={{ padding: '4px 12px' }}>
          {errorString}
        </Alert>
      ) : null}

      <Form.Field.Input
        variant="outlined"
        name={`${taskId}.subject`}
        label="Subject"
        fullWidth={true}
        onChange={(val) => onChange(val, 'subject')}
        showPicker={true}
        options={replacementVariables}
        optValue="value"
        optLabel="text"
        emojiPicker={true}
      />
      <Form.Field.TextEditor
        getChildEditorRef={getChildEditorRef}
        name={taskId.content ? `${taskId}.content` : `${taskId}.contentOriginal`}
        onChange={(val) => onChange(val, 'content')}
        ref={textEditorRef}
      />
      <Form.Field.File name={`${taskId}.attachments`} maxSize={8} />
    </Box>
  );
});

export default Email;
